import { type IGeoreference } from "@interfaces/common/IGeoreference";
import { type ISettings } from "@interfaces/common/ISettings";
import { type ITypeState } from "@interfaces/common/ITypeState";
import { type IAccessibleModule } from "@interfaces/modules/IAccessibleModule";
import { type IPlanStatus } from "@interfaces/modules/IGetModulesResponse";
import {
  Action,
  createSelector,
  Selector,
  State,
  type StateContext,
} from "@ngxs/store";
import { FavoritesService } from "@services/favorites.service";
import { catchError, of, tap } from "rxjs";

import { HttpErrorResponse } from "@angular/common/http";
import { inject, Service } from "@angular/core";

import {
  ClearAccessibleModules,
  ClearCompanyGeoreference,
  ClearFavorites,
  ClearSavedRut,
  GetFavorites,
  SaveFavorites,
  SetAccessibleModules,
  SetCompanyGeoreference,
  SetCurrentTenant,
  SetCurrentTenantBusinessName,
  SetPlanStatus,
  SetSavedRut,
  SetSettings,
  SetTypeState,
} from "./common.action";

export interface CommonStateModel {
  typeStates: ITypeState[];
  settings: ISettings | null;
  currentTenant: string | null;
  currentTenantBusinessName: string | null;
  savedRut: string | null;
  accessibleModules: IAccessibleModule[];
  planStatus: IPlanStatus | null;
  companyGeoreference: IGeoreference | null;
  favorites: string[];
}

@State<CommonStateModel>({
  name: "Common",
  defaults: {
    typeStates: [],
    settings: null,
    currentTenant: null,
    currentTenantBusinessName: null,
    savedRut: null,
    accessibleModules: [],
    planStatus: null,
    companyGeoreference: null,
    favorites: [],
  },
})
@Service({ autoProvided: false })
export class CommonState {
  private favoritesService = inject(FavoritesService);

  @Selector()
  static typeStates(state: CommonStateModel) {
    return state.typeStates;
  }

  @Selector()
  static settings(state: CommonStateModel) {
    return state.settings;
  }

  @Selector()
  static currentTenant(state: CommonStateModel) {
    return state.currentTenant;
  }

  @Selector()
  static currentTenantBusinessName(state: CommonStateModel) {
    return state.currentTenantBusinessName;
  }

  @Selector()
  static savedRut(state: CommonStateModel) {
    return state.savedRut;
  }

  @Selector()
  static accessibleModules(state: CommonStateModel) {
    return state.accessibleModules;
  }

  @Selector()
  static accessibleModuleNames(state: CommonStateModel): Set<string> {
    return new Set(state.accessibleModules.map(m => m.name));
  }

  @Selector()
  static accessibleRouteKeys(state: CommonStateModel): Set<string> {
    return new Set(state.accessibleModules.map(m => m.routeKey));
  }

  @Selector()
  static planStatus(state: CommonStateModel) {
    return state.planStatus;
  }

  @Selector()
  static companyGeoreference(state: CommonStateModel) {
    return state.companyGeoreference;
  }

  @Selector()
  static favoriteKeys(state: CommonStateModel): string[] {
    return state.favorites;
  }

  static getTypeStateByDescription(description: string) {
    return createSelector([CommonState], (state: CommonStateModel) =>
      state.typeStates.find(typeState => typeState.description === description)
    );
  }

  @Action(SetTypeState)
  setTypeState(ctx: StateContext<CommonStateModel>, action: SetTypeState) {
    ctx.patchState({
      typeStates: action.typeStates,
    });
  }

  @Action(SetSettings)
  setSettings(ctx: StateContext<CommonStateModel>, action: SetSettings) {
    ctx.patchState({
      settings: action.settings,
    });
  }

  @Action(SetCurrentTenant)
  setCurrentTenant(
    ctx: StateContext<CommonStateModel>,
    action: SetCurrentTenant
  ) {
    const tenantChanged = ctx.getState().currentTenant !== action.currentTenant;
    const statePatch: Partial<CommonStateModel> = {
      currentTenant: action.currentTenant,
    };

    if (tenantChanged) {
      Object.assign(statePatch, {
        currentTenantBusinessName: null,
        accessibleModules: [],
        planStatus: null,
        companyGeoreference: null,
        favorites: [],
      });
    }

    ctx.patchState(statePatch);
  }

  @Action(SetSavedRut)
  setSavedRut(ctx: StateContext<CommonStateModel>, action: SetSavedRut) {
    ctx.patchState({
      savedRut: action.rut,
    });
  }

  @Action(ClearSavedRut)
  clearSavedRut(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      savedRut: null,
    });
  }

  @Action(SetCurrentTenantBusinessName)
  setCurrentTenantBusinessName(
    ctx: StateContext<CommonStateModel>,
    action: SetCurrentTenantBusinessName
  ) {
    ctx.patchState({
      currentTenantBusinessName: action.businessName,
    });
  }

  @Action(SetAccessibleModules)
  setAccessibleModules(
    ctx: StateContext<CommonStateModel>,
    action: SetAccessibleModules
  ) {
    ctx.patchState({ accessibleModules: action.accessibleModules });
  }

  @Action(ClearAccessibleModules)
  clearAccessibleModules(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      accessibleModules: [],
    });
  }

  @Action(SetPlanStatus)
  setPlanStatus(ctx: StateContext<CommonStateModel>, action: SetPlanStatus) {
    ctx.patchState({
      planStatus: action.planStatus,
    });
  }

  @Action(SetCompanyGeoreference)
  setCompanyGeoreference(
    ctx: StateContext<CommonStateModel>,
    action: SetCompanyGeoreference
  ) {
    ctx.patchState({
      companyGeoreference: action.companyGeoreference,
    });
  }

  @Action(ClearCompanyGeoreference)
  clearCompanyGeoreference(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      companyGeoreference: null,
    });
  }

  @Action(GetFavorites, { cancelUncompleted: true })
  getFavorites(ctx: StateContext<CommonStateModel>, action: GetFavorites) {
    const tenant = action.tenant ?? ctx.getState().currentTenant;

    if (!tenant) {
      return;
    }

    return this.favoritesService.getFavorites(tenant).pipe(
      tap(res => ctx.patchState({ favorites: res.data })),
      catchError(err => {
        if (!(err instanceof HttpErrorResponse && err.status === 500)) {
          console.error("[GetFavorites] Error fetching favorites:", err);
        }

        ctx.patchState({ favorites: [] });
        return of(null);
      })
    );
  }

  @Action(SaveFavorites)
  saveFavorites(ctx: StateContext<CommonStateModel>, action: SaveFavorites) {
    const tenant = ctx.getState().currentTenant;

    if (!tenant) {
      return;
    }

    const previousFavorites = ctx.getState().favorites;

    ctx.patchState({
      favorites: action.favoriteKeys,
    });

    return this.favoritesService
      .saveFavorites(tenant, action.favoriteKeys)
      .pipe(
        tap({
          next: res => {
            ctx.patchState({
              favorites: res.data,
            });
          },
          error: err => {
            console.error("[SaveFavorites] Error saving favorites:", err);
            ctx.patchState({
              favorites: previousFavorites,
            });
          },
        })
      );
  }

  @Action(ClearFavorites)
  clearFavorites(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      favorites: [],
    });
  }
}
